import { portalFetch } from '../lib/apiClient';
import { usePortalAuthStore } from '../stores/portalAuthStore';

interface TranslationsResponse {
	languageId: number;
	languageCode: string;
	languageName: string;
	translations: Record<string, string>;
}

interface ActiveLanguage {
	id: number;
	code: string;
	name: string;
	isActive: boolean;
}

/**
 * Fetch translations for a language code via the portal API.
 * Used as the custom fetchTranslations callback for TranslationProvider.
 */
export async function getTranslations(
	code: string,
	bustCache = false,
): Promise<Record<string, string>> {
	const token = usePortalAuthStore.getState().token;

	if (token == null) {
		return {};
	}

	const url = bustCache
		? `/language/code/${code}?t=${Date.now()}`
		: `/language/code/${code}`;

	const response = await portalFetch<TranslationsResponse>(url, token);
	return response.translations;
}

/**
 * Get the user's preferred language from the portal auth store.
 * Used as the custom fetchPreferredLanguage callback for TranslationProvider.
 */
export async function getPreferredLanguage(): Promise<string> {
	const store = usePortalAuthStore.getState();

	// Check localStorage-persisted language preference first
	const storedLanguage = store.languagePreference;
	if (storedLanguage != null) {
		return storedLanguage;
	}

	// Fall back to browser language detection
	if (navigator.languages != null && navigator.languages.length > 0) {
		return navigator.languages[0].split('-')[0];
	}

	if (navigator.language != null) {
		return navigator.language.split('-')[0];
	}

	return 'en';
}

/**
 * Fetch the list of active languages available for the portal.
 */
export async function getActiveLanguages(
	token: string,
): Promise<Array<ActiveLanguage>> {
	const response = await portalFetch<{
		languages: Array<ActiveLanguage>;
	}>('/language', token);
	return response.languages;
}

/**
 * Update the user's language preference on the server.
 */
export async function updateLanguagePreference(
	token: string,
	languageCode: string,
): Promise<void> {
	await portalFetch<{ success: boolean }>('/language/preference', token, {
		method: 'PUT',
		body: JSON.stringify({ languageCode }),
	});
}
